import { FetchPolicy, useLazyQuery } from '@apollo/client'
import { useEffect } from 'react'

import { GetAccountsPayableSystemSuppliersDocument } from '@/graphql/purchase-plus/generated/getAccountsPayableSystemSuppliers.generated'
import {
  SortDirectionEnum,
  Supplier,
  SuppliersQuerySortsPropertyEnum,
  TradeRelationshipStatusEnum,
} from '@/graphql/purchase-plus/generated/purchasePlus_graphql'
import { RowsPerPage } from '@/modules/shared/components/table/types'
import { DEFAULT_ROWS_PER_PAGE, PURCHASE_PLUS_GRAPHQL_API } from '@/modules/shared/constants'
import { useWatchQueryParams } from '@/modules/shared/hooks/useWatchQueryParams'
import { checkNetworkStatus } from '@/modules/shared/utils/checkNetworkStatus'
import { extractEdges } from '@/modules/shared/utils/extractEdges'
import { ignoreSingleCharacter } from '@/modules/shared/utils/ignoreSingleCharacter'
import { onFetchMore, onNextPage, onPreviousPage } from '@/modules/shared/utils/paginationUtils'

interface UseGetAccountsPayableSystemSuppliersProps extends RowsPerPage {
  lazyQuery?: boolean
  fetchPolicy?: FetchPolicy
  status?: TradeRelationshipStatusEnum[]
  isVerified?: boolean | null
  preferredSupplier?: boolean | null
  purchaserId?: number
  accountsPayableSystemId?: number
  searchText?: string
  enabled?: boolean
  ignoreSearchValueFromParams?: boolean
}

export const useGetAccountsPayableSystemSuppliers = (args?: UseGetAccountsPayableSystemSuppliersProps) => {
  const {
    lazyQuery,
    fetchPolicy,
    status,
    purchaserId,
    accountsPayableSystemId,
    preferredSupplier,
    isVerified,
    searchText,
    enabled,
    ignoreSearchValueFromParams,
    rowsPerPage,
  } = args || {}
  const paginationResultsPerPage = rowsPerPage || DEFAULT_ROWS_PER_PAGE

  const { searchValue, businessNumber, supplierNumber } = useWatchQueryParams()

  const [fetchAccountsPayableSystemSuppliers, { data, error, fetchMore, networkStatus, refetch }] = useLazyQuery(
    GetAccountsPayableSystemSuppliersDocument,
    {
      variables: {
        first: paginationResultsPerPage,
        after: null,
        filters: {
          searchTextFuzzy: ignoreSingleCharacter(searchText || (ignoreSearchValueFromParams ? undefined : searchValue)),
          enabled,
          preferredSupplier,
          isVerified,
          purchaserIdEq: purchaserId,
          statusIn: status,
          businessNumberEq: businessNumber,
          supplierNumberEq: supplierNumber,
        },
        accountsPayableSystemId,
        sorts: [{ property: SuppliersQuerySortsPropertyEnum.Name, direction: SortDirectionEnum.Asc }],
      },
      context: {
        uri: PURCHASE_PLUS_GRAPHQL_API,
      },
      fetchPolicy: fetchPolicy || 'cache-and-network',
      notifyOnNetworkStatusChange: true,
    }
  )

  useEffect(() => {
    if (!lazyQuery) {
      fetchAccountsPayableSystemSuppliers()
    }
  }, [])

  const pageInfo = data?.accountsPayableSystem?.suppliers?.pageInfo

  return {
    fetchAccountsPayableSystemSuppliers,
    suppliers: extractEdges<Supplier>(data?.accountsPayableSystem?.suppliers),
    refetch,
    loadingStates: checkNetworkStatus(networkStatus),
    error,
    paginationResultsPerPage,
    pageInfo,
    networkStatus,
    onNextPage: () =>
      onNextPage({
        endCursor: pageInfo?.endCursor || null,
        refetch,
        paginationResultsPerPage,
      }),
    onPreviousPage: () =>
      onPreviousPage({
        startCursor: pageInfo?.startCursor || null,
        refetch,
        paginationResultsPerPage,
      }),
    onFetchMore: () =>
      onFetchMore({
        endCursor: pageInfo?.endCursor || null,
        fetchMore,
        paginationResultsPerPage,
      }),
  }
}
